// src/services/Integrations/LevInsIntegration.ts

import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";

const BASE = `${CALCULATOR_BASE_URL}/l-ins`;

const post = async <T>(jwt: string, path: string, body: object): Promise<T> => {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Лев Инс ${path}: HTTP ${res.status}${text ? ` - ${text}` : ""}`);
  }
  const json = await res.json();
  // JSON-RPC errors can arrive on HTTP 200 as { code, message }
  if (json && typeof json === "object" && "code" in json && "message" in json) {
    throw new Error((json as { message: string }).message);
  }
  return json as T;
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LevInsAddress {
  address_type: string;        // "H" = home, "C" = correspondence
  country_id: string;          // "BG"
  country_name: string;
  state_id?: string;
  state_name?: string;
  region_id?: string;
  region_name?: string;
  city_id?: string;
  city_name?: string;
  address?: string;
  post_code?: string;
  phone?: string;
  e_mail?: string;
}

export interface LevInsInsurer {
  person_id: string;           // EGN / Bulstat
  id_type: "EGN" | "BULSTAT";
  person_type: "1" | "2";      // 1 = physical, 2 = legal
  man_id?: string;
  name: string;
  gname?: string;
  sname?: string;
  fname?: string;
  sex?: "1" | "2";             // 1 = female, 2 = male
  birth_date?: string;
  name_eng?: string;
  addresses?: LevInsAddress[];
}

export interface LevInsVehicleOwner {
  person_id: string;
  man_id?: string;
  name?: string;
  gname?: string;
  sname?: string;
  fname?: string;
  own_percent?: number;
}

export interface LevInsObject {
  reg_no: string;
  cert_no: string;
  brand?: string;
  model?: string;
  chassis?: string;
  type_id?: string;            // vehicle type ID
  body_type_id?: string;
  seats_num?: string;
  color_id?: string;
  paint_type_id?: string;
  reg_type_id?: string;
  usage_id?: string;           // usage/purpose ID
  fuel_type_id?: string;       // "B"=benzin, "D"=diesel, "GB"=gas-benzin, etc.
  ccm_ton?: string;
  engine_power?: string;
  tonage?: string;
  first_reg?: string;          // "DD-MM-YYYY"
  prod_year?: string;
  modification?: string | null;
  drive_region_id?: string;
  wheel_position_id?: string;  // "1"=left, "2"=right
  engine?: string | null;
  owners?: LevInsVehicleOwner[];
  vehicle_users?: unknown[];
  insured_seats?: number | null;
}

export interface LevInsPolicyData {
  insr_type: "1000";
  from_date: string;           // "DD-MM-YYYY HH:MM"
  to_date: string;             // "DD-MM-YYYY"
  currency?: string;           // "EUR"
  installments?: number;
  certificate_no?: string;
  sticker_no?: string | null;
  insurer: LevInsInsurer;
  objects: LevInsObject[];
  covers: { cover_id: string; limit: string }[];
  corrections?: unknown[];
}

export interface LevInsPremiumResponse {
  result: {
    PREMIUM_TOTAL?: string;
    CURRENCY?: string;
    INSTALLMENTS?: {
      INSTALLMENT_NUM: number;
      INSTALLMENT_DATE: string;
      INSTALLMENT_PREMIUM: string;
    }[];
    [key: string]: unknown;
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export const LevInsIntegrationService = {
  /** Calculate MTPL premium - /premium expects the { policyData } wrapper,
   *  unlike /policies and /policies/offer which take the flat object */
  getPremium: (jwt: string, policyData: LevInsPolicyData): Promise<LevInsPremiumResponse> =>
    post(jwt, "/premium", { policyData }),

  /** Issue policy - requires sticker_no in policyData */
  savePolicy: (jwt: string, policyData: LevInsPolicyData): Promise<any> =>
    post(jwt, "/policies", policyData),

  /** Get installment plan */
  getInstallments: (jwt: string, policyData: LevInsPolicyData): Promise<any> =>
    post(jwt, "/installments", policyData),

  /** Print offer PDF - returns { result: Blob } */
  printOffer: async (jwt: string, policyData: LevInsPolicyData): Promise<Blob> => {
    const res = await fetch(`${BASE}/policies/offer`, {
      method: "POST",
      headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
      body: JSON.stringify(policyData),
    });
    if (!res.ok) throw new Error(`Лев Инс печат: HTTP ${res.status}`);
    return res.blob();
  },

  /** Print policy PDF - returns { result: Blob } */
  printPolicy: (jwt: string, policyData: object): Promise<any> =>
    post(jwt, "/policies/print", policyData),

  /** Get vehicle details */
  getVehicle: (jwt: string, params: object): Promise<any> =>
    post(jwt, "/vehicles/details", params),

  // ── Nomenclatures ───────────────────────────────────────────────────────
  getVehicleTypes: (jwt: string): Promise<any> => post(jwt, "/vehicles/types", {}),
  getVehicleFuelTypes: (jwt: string): Promise<any> => post(jwt, "/vehicles/fuel-types", {}),
  getVehicleUsages: (jwt: string): Promise<any> => post(jwt, "/vehicles/usages", {}),
  getVehicleColors: (jwt: string): Promise<any> => post(jwt, "/vehicles/colors", {}),
  getVehicleBodyTypes: (jwt: string): Promise<any> => post(jwt, "/vehicles/body-types", {}),
  getVehicleRegTypes: (jwt: string): Promise<any> => post(jwt, "/vehicles/registration-types", {}),
  getVehicleDriveRegions: (jwt: string): Promise<any> => post(jwt, "/vehicles/drive-regions", {}),
};
